import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { InvestorRmdStatusViewModel, AccountRmdStatusViewModel, RMDTrackingViewModel, NoteViewModel, HouseholdRmdFcaViewModel } from '../../shared/models/rmd.models';
import { IUser } from '../models/identity.models';
import { formatDate } from '@angular/common';
import * as pako from 'pako';

@Injectable({
  providedIn: 'root', // Makes the service available application-wide
})
export class RmdTrackerApiService {
  private readonly apiUrl = `/api/rmdtracker`;
  private readonly servicerBaseUrl = '/api/servicer';
  private cache: { [key: string]: any } = {};

  constructor(private http: HttpClient) { }


  /**
 * Fetch RMD Tracking Data
 * @param adminMode - Boolean indicating admin mode
 * @param firmId - ID of the firm
 * @returns Observable with the tracking data
 */

  getFreshCompressedRMDTrackingData(adminMode: boolean, firmId: number, user: IUser | undefined, clearCache = false): Observable<any> {
    // Always use server compression
    const useServerCompression = true;

    function decompressServerData(compressedData: string): any {
      try {
        const binaryString = atob(compressedData); // Decode Base64
        const binaryData = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          binaryData[i] = binaryString.charCodeAt(i);
        }
        const decompressedText = pako.inflate(binaryData, { to: "string" }); // GZip decompression
        return JSON.parse(decompressedText);
      } catch (error) {
        console.error("Error decompressing server data", error);
        return null;
      }
    }

    return this.http
      .get<{ cacheDate: string; data: string }>(`${this.apiUrl}/rmdmonitor/${adminMode}/${firmId}/${clearCache}/${useServerCompression}`)
      .pipe(
        map((response) => decompressServerData(response.data)) // Always decompress the server response
      );
  }

  updateInvestorRMDStatus(rmdStatusViewModel: InvestorRmdStatusViewModel): Observable<InvestorRmdStatusViewModel> {
    return this.http.post<InvestorRmdStatusViewModel>(`${this.servicerBaseUrl}/updateinvestorrmdstatus`, rmdStatusViewModel).pipe(
      tap(() => {
        sessionStorage.clear(); // Clear session storage only after a successful API response
        console.log('Session storage cleared after updating investor RMD status.');
      })
    );
  }

  updateAccountRMDStatus(firmId: number, rmdStatusViewModel: AccountRmdStatusViewModel): Observable<AccountRmdStatusViewModel> {
    return this.http.post<AccountRmdStatusViewModel>(`${this.servicerBaseUrl}/updateaccountrmdstatus/${firmId}`, rmdStatusViewModel);
  }

  updateHouseholdFca(firmId: number, model: HouseholdRmdFcaViewModel): Observable<HouseholdRmdFcaViewModel> {
    return this.http.post<HouseholdRmdFcaViewModel>(`${this.servicerBaseUrl}/updatehouseholdfca/${firmId}`, model);
  }

  updateDoubleInherited(firmId: number, rmdStatusViewModel: AccountRmdStatusViewModel): Observable<AccountRmdStatusViewModel> {
    return this.http.post<AccountRmdStatusViewModel>(`${this.servicerBaseUrl}/updateaccountdoubleinherited/${firmId}`, rmdStatusViewModel);
  }

  addNote(note: NoteViewModel): Observable<NoteViewModel> {
    return this.http.post<NoteViewModel>(`${this.servicerBaseUrl}/addnoteforaccount`, note);
  }

  updateNote(note: NoteViewModel): Observable<NoteViewModel> {
    return this.http.post<NoteViewModel>(`${this.servicerBaseUrl}/updatenoteforaccount`, note);
  }

  
}
